/**
 * Eased horizontal scrolling, shared by the two places that nudge a sideways
 * scroller on mount to show there is more of it than fits: the hero pills and
 * the header's workspace-type switcher.
 *
 * `scrollTo({ behavior: 'smooth' })` is not enough for either — the duration is
 * the browser's to pick, so the out-and-back can't be timed, and it can't be
 * cancelled when the user takes over mid-hint.
 */

const easeInOutCubic = (t: number) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/**
 * Animates `el.scrollLeft` to `target` over `duration` ms.
 *
 * @param onDone runs when the animation reaches the target, and never if it
 * was cancelled. Chain a follow-up leg off this rather than off a second timer
 * started alongside the first: on a busy main thread both timers come due
 * while it is blocked and then fire back to back, so the second leg wipes out
 * the first before any of it has been drawn.
 * @returns a cancel function — call it to stop mid-flight and leave the
 * scroller wherever it got to. Safe to call after the animation has finished.
 */
export const animateScrollLeft = (
    el: HTMLElement,
    target: number,
    duration: number,
    onDone?: () => void
): (() => void) => {
    const from = el.scrollLeft;
    const delta = target - from;
    if (delta === 0 || duration <= 0) {
        el.scrollLeft = target;
        onDone?.();
        return () => {};
    }

    const startedAt = performance.now();
    let rafId = requestAnimationFrame(function step(now: number) {
        const progress = Math.min(1, (now - startedAt) / duration);
        el.scrollLeft = from + delta * easeInOutCubic(progress);
        if (progress < 1) {
            rafId = requestAnimationFrame(step);
        } else {
            onDone?.();
        }
    });

    return () => cancelAnimationFrame(rafId);
};
